import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { API_URL, BOT_API_URL } from "../config/api";

const SidebarContext = createContext(null);

const readJsonSafe = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
        return null;
    }

    return response.json();
};

const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, options);

    if (!response.ok) {
        const data = await readJsonSafe(response);

        throw new Error(
            data?.message ||
                data?.error ||
                `Erreur HTTP ${response.status}`,
        );
    }

    return readJsonSafe(response);
};

const getStoredGuild = () => {
    const stored = localStorage.getItem("selectedGuild");

    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch (error) {
        console.error(
            "❌ Erreur de lecture du serveur sélectionné :",
            error,
        );
        localStorage.removeItem("selectedGuild");
        return null;
    }
};

const isEnabled = (value) =>
    value === true || value === 1 || value === "1";

const hasActivePremium = (server) => {
    if (isEnabled(server?.isPremium)) {
        return true;
    }

    if (!server?.premium_until) {
        return false;
    }

    const expirationDate = new Date(server.premium_until);

    return (
        !Number.isNaN(expirationDate.getTime()) &&
        expirationDate > new Date()
    );
};

const normalizeBotGuildIds = (data) => {
    const source = Array.isArray(data)
        ? data
        : Array.isArray(data?.guildIds)
          ? data.guildIds
          : Array.isArray(data?.guilds)
            ? data.guilds
            : [];

    return source
        .map((item) => {
            if (
                item &&
                typeof item === "object" &&
                item.id !== undefined
            ) {
                return String(item.id);
            }

            return item !== undefined && item !== null
                ? String(item)
                : null;
        })
        .filter(Boolean);
};

export const SidebarProvider = ({ children }) => {
    const [guilds, setGuilds] = useState([]);
    const [collaborations, setCollaborations] = useState([]);
    const [botGuildIds, setBotGuildIds] = useState([]);

    const [selectedGuild, setSelectedGuild] = useState(
        getStoredGuild,
    );
    const [isOwner, setIsOwner] = useState(false);

    const [sidebarDataLoaded, setSidebarDataLoaded] =
        useState(false);
    const [selectionLoaded, setSelectionLoaded] =
        useState(false);
    const [isRefreshingSidebar, setIsRefreshingSidebar] =
        useState(false);
    const [sidebarError, setSidebarError] = useState("");

    const loadedSidebar =
        sidebarDataLoaded && selectionLoaded;

    const loadSidebarData = useCallback(
        async ({ signal, showLoader = true } = {}) => {
            const token =
                localStorage.getItem("access_token");

            if (!token) {
                setGuilds([]);
                setCollaborations([]);
                setBotGuildIds([]);
                setSidebarDataLoaded(true);
                setIsRefreshingSidebar(false);
                return;
            }

            if (showLoader) {
                setSidebarDataLoaded(false);
            } else {
                setIsRefreshingSidebar(true);
            }

            setSidebarError("");

            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const optionalRequest = async (
                request,
                fallback,
                label,
            ) => {
                try {
                    return await request;
                } catch (error) {
                    if (error.name === "AbortError") {
                        throw error;
                    }

                    console.error(
                        `❌ Erreur chargement ${label} :`,
                        error,
                    );
                    return fallback;
                }
            };

            try {
                /*
                 * Les guilds sont indispensables.
                 * Les collaborations et statuts Premium restent
                 * facultatifs pour éviter de bloquer toute la sidebar.
                 */
                const [
                    guildsData,
                    collaborationsData,
                    serversData,
                ] = await Promise.all([
                    fetchJson(
                        `${API_URL}/api/discord/guilds`,
                        {
                            credentials: "include",
                            headers,
                            signal,
                        },
                    ),
                    optionalRequest(
                        fetchJson(
                            `${API_URL}/api/collaborations`,
                            {
                                credentials: "include",
                                headers: {
                                    ...headers,
                                    "Content-Type":
                                        "application/json",
                                },
                                signal,
                            },
                        ),
                        [],
                        "des collaborations",
                    ),
                    optionalRequest(
                        fetchJson(`${API_URL}/api/servers`, {
                            credentials: "include",
                            headers,
                            signal,
                        }),
                        [],
                        "des serveurs Nexus",
                    ),
                ]);

                const safeGuilds = Array.isArray(guildsData)
                    ? guildsData
                    : [];
                const safeCollaborations = Array.isArray(
                    collaborationsData,
                )
                    ? collaborationsData
                    : [];
                const safeServers = Array.isArray(serversData)
                    ? serversData
                    : [];

                const serverMap = new Map(
                    safeServers.map((server) => [
                        String(server.discord_guild_id),
                        server,
                    ]),
                );

                const enrichedGuilds = safeGuilds.map(
                    (guild) => {
                        const server = serverMap.get(
                            String(guild.id),
                        );

                        return {
                            ...guild,
                            isPremium:
                                hasActivePremium(server),
                            premium_until:
                                server?.premium_until || null,
                        };
                    },
                );

                /*
                 * BOT_API_URL dépend de la liste des serveurs
                 * Discord, donc cette requête vient ensuite.
                 * Son échec ne bloque pas le reste de la sidebar.
                 */
                const allowedUserGuildIds = safeGuilds.map(
                    (guild) => String(guild.id),
                );

                const botGuildsData = await optionalRequest(
                    fetchJson(`${BOT_API_URL}/bot/guilds`, {
                        method: "POST",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            userGuildIds:
                                allowedUserGuildIds,
                        }),
                        signal,
                    }),
                    [],
                    "des serveurs du bot",
                );

                if (signal?.aborted) return;

                setGuilds(enrichedGuilds);
                setCollaborations(safeCollaborations);
                setBotGuildIds(
                    normalizeBotGuildIds(botGuildsData),
                );
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error(
                        "❌ Erreur chargement SidebarContext :",
                        error,
                    );

                    setSidebarError(error.message);
                    setGuilds([]);
                    setCollaborations([]);
                    setBotGuildIds([]);
                }
            } finally {
                if (!signal?.aborted) {
                    setSidebarDataLoaded(true);
                    setIsRefreshingSidebar(false);
                }
            }
        },
        [],
    );

    const refreshSidebar = useCallback(() => {
        return loadSidebarData({
            showLoader: false,
        });
    }, [loadSidebarData]);

    const selectGuild = useCallback((guild) => {
        if (guild) {
            localStorage.setItem(
                "selectedGuild",
                JSON.stringify(guild),
            );
        } else {
            localStorage.removeItem("selectedGuild");
        }

        window.dispatchEvent(
            new CustomEvent("selectedGuildChanged"),
        );
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        loadSidebarData({
            signal: controller.signal,
        });

        return () => controller.abort();
    }, [loadSidebarData]);

    /*
     * Synchronisation du serveur sélectionné
     * et vérification de son propriétaire.
     */
    useEffect(() => {
        let ownershipController = null;

        const updateSelectedGuild = async () => {
            ownershipController?.abort();

            const controller = new AbortController();
            ownershipController = controller;

            setSelectionLoaded(false);
            setIsOwner(false);

            const storedGuild = getStoredGuild();
            const token =
                localStorage.getItem("access_token");

            if (!storedGuild) {
                setSelectedGuild(null);
                setSelectionLoaded(true);
                return;
            }

            setSelectedGuild(storedGuild);

            if (!token) {
                setSelectionLoaded(true);
                return;
            }

            try {
                const data = await fetchJson(
                    `${API_URL}/api/collaborations/${storedGuild.id}/is-owner`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        signal: controller.signal,
                    },
                );

                if (!controller.signal.aborted) {
                    setIsOwner(
                        data?.isOwner === true ||
                            data?.isOwner === 1 ||
                            data?.isOwner === "1",
                    );
                }
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error(
                        "❌ Erreur vérification ownership :",
                        error,
                    );
                    setIsOwner(false);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setSelectionLoaded(true);
                }
            }
        };

        const handleStorageChange = (event) => {
            if (
                !event.key ||
                event.key === "selectedGuild"
            ) {
                updateSelectedGuild();
            }
        };

        updateSelectedGuild();

        window.addEventListener(
            "selectedGuildChanged",
            updateSelectedGuild,
        );
        window.addEventListener(
            "storage",
            handleStorageChange,
        );

        return () => {
            ownershipController?.abort();

            window.removeEventListener(
                "selectedGuildChanged",
                updateSelectedGuild,
            );
            window.removeEventListener(
                "storage",
                handleStorageChange,
            );
        };
    }, []);

    /*
     * Quand les données Premium arrivent, le serveur
     * sélectionné reçoit également les informations enrichies.
     */
    useEffect(() => {
        if (guilds.length === 0) return;

        setSelectedGuild((currentGuild) => {
            if (!currentGuild?.id) return currentGuild;

            const enrichedGuild = guilds.find(
                (guild) =>
                    String(guild.id) ===
                    String(currentGuild.id),
            );

            if (!enrichedGuild) return currentGuild;

            return {
                ...currentGuild,
                ...enrichedGuild,
            };
        });
    }, [guilds]);

    /*
     * Nettoyage immédiat du contexte lors de la déconnexion.
     */
    useEffect(() => {
        const handleLogout = () => {
            setGuilds([]);
            setCollaborations([]);
            setBotGuildIds([]);
            setSelectedGuild(null);
            setIsOwner(false);
            setSidebarError("");
            setSidebarDataLoaded(true);
            setSelectionLoaded(true);
        };

        window.addEventListener(
            "userLoggedOut",
            handleLogout,
        );

        return () => {
            window.removeEventListener(
                "userLoggedOut",
                handleLogout,
            );
        };
    }, []);

    const contextValue = useMemo(
        () => ({
            guilds,
            collaborations,
            botGuildIds,
            selectedGuild,
            isOwner,
            loadedSidebar,

            // Propriétés supplémentaires
            isRefreshingSidebar,
            sidebarError,
            refreshSidebar,
            selectGuild,
        }),
        [
            botGuildIds,
            collaborations,
            guilds,
            isOwner,
            isRefreshingSidebar,
            loadedSidebar,
            refreshSidebar,
            selectGuild,
            selectedGuild,
            sidebarError,
        ],
    );

    return (
        <SidebarContext.Provider value={contextValue}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => {
    const context = useContext(SidebarContext);

    if (!context) {
        throw new Error(
            "useSidebar doit être utilisé à l’intérieur de SidebarProvider.",
        );
    }

    return context;
};